import Env from "@/config/environment";
import { getToken } from "../auth/jwt";
// Only read inside a request, never while modules load: i18n imports the auth
// hooks, which import this file.
import i18n from "./i18n";

export const fetchServer = async (
  url: string,
  options: RequestInit & { ignoreError?: boolean; timeout?: number } = {
    method: "GET",
  },
): Promise<Response> => {
  const domain = Env.server.replace(/\/$/, "");

  // Set up abort controller for timeout
  const controller = new AbortController();
  const timeoutMs = options.timeout ?? 30000; // Default 30 seconds, allow override
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  try {
    options.headers = {
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
      ...(options?.body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...options.headers,
    };

    // Add abort signal to options
    options.signal = controller.signal;

    const data = await fetch(domain + url, options);

    if (!data.ok && !options?.ignoreError) {
      if (data.status === 401 || data.status === 403) {
        const noRedirectWhitelist = [
          "/api/users/v1/clients",
          "/api/users/v1/auth/renew",
        ];
        if (!noRedirectWhitelist.includes(url)) {
          document.location.href = "/";
        }
      }
      let errorData = "";
      let errorCode: string | undefined;
      try {
        const body = await data.json();
        errorData = body.error || body.message || JSON.stringify(body);
        // A route that knows *why* it refused says so in `code`, for the screen
        // to explain in the user's language (e.g. the calendar's booking errors).
        if (typeof body.code === "string") errorCode = body.code;
      } catch {
        // Ignore JSON parsing errors and fallback to status text
      }
      throw Object.assign(
        new Error(
          errorData ||
            i18n.t("common.network.serverError", {
              status: `${data.status} ${data.statusText}`.trim(),
              defaultValue: "Server error: {{status}}",
            }),
        ),
        { code: errorCode, status: data.status },
      );
    }

    clearTimeout(timeoutId);
    return data;
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle abort/timeout errors
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        throw new Error(
          i18n.t("common.network.timeout", {
            defaultValue:
              "Network timeout: The request took too long to complete",
          }),
        );
      }
      // Re-throw network errors with more descriptive messages
      if (error.message.includes("fetch")) {
        throw new Error(
          i18n.t("common.network.unreachable", {
            defaultValue: "Network error: Unable to connect to the server",
          }),
        );
      }
    }

    throw error;
  }
};

export const getServerUrl = (url: string) => {
  const domain = Env.server.replace(/\/$/, "");
  return domain + url;
};

export const api = {
  get: async <V>(url: string, options?: RequestInit) => {
    const res = await fetchServer(url, { ...options, method: "GET" });
    return res.json() as Promise<V>;
  },
  post: async <V, T>(
    url: string,
    body?: T,
    options?: RequestInit & {
      timeout?: number;
    },
  ) => {
    const res = await fetchServer(url, {
      ...options,
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
    return res.json() as Promise<V>;
  },
  put: async <V, T>(url: string, body?: T, options?: RequestInit) => {
    const res = await fetchServer(url, {
      ...options,
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
    return res.json() as Promise<V>;
  },
  patch: async <V, T>(url: string, body?: T, options?: RequestInit) => {
    const res = await fetchServer(url, {
      ...options,
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    });
    return res.json() as Promise<V>;
  },
  delete: async <V>(url: string, options?: RequestInit) => {
    const res = await fetchServer(url, { ...options, method: "DELETE" });
    return res.json() as Promise<V>;
  },
};
